import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell, { PageContainer, PageHeader } from "@/components/AppShell";
import { useAuth } from "@/lib/AuthContext";
import { useT } from "@/lib/i18n";
import { useLoginWall } from "@/components/LoginWall";
import { Button } from "@/components/ui/button";
import { Crown, Check, Zap, Bot, Users, Swords, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function PremiumPage() {
  const { t } = useT();
  const { user, setPremium } = useAuth();
  const { requireAuth } = useLoginWall();
  const navigate = useNavigate();
  const isGuest = !user;
  const [loadingPlan, setLoadingPlan] = useState(null);

  const handleSubscribe = async (plan) => {
    if (isGuest) {
      requireAuth({
        feature: "premium",
        title: t("wall.title"),
        sub: t("guest.premium_footer"),
      });
      return;
    }
    if (user?.isAdmin) {
      toast.success(t("page.premium.admin_badge") || "Admin · Premium dauerhaft aktiv.");
      return;
    }
    setLoadingPlan(plan);
    try {
      const res = await api.post("/payments/checkout/session", {
        package_id: "premium_monthly",
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      throw new Error("missing_checkout_url");
    } catch (err) {
      const msg = err?.response?.data?.detail?.message || err?.message;
      toast.error(msg || (t("premium.checkout_failed") || "Checkout konnte nicht gestartet werden."));
      setLoadingPlan(null);
    }
  };

  const PERKS = [
    { icon: Bot, title: t("page.premium.unlimited_coach"), desc: t("page.premium.unlimited_coach_d") },
    { icon: Users, title: t("page.premium.priority_match"), desc: t("page.premium.priority_match_d") },
    { icon: Swords, title: t("page.premium.adv_meta"), desc: t("page.premium.adv_meta_d") },
    { icon: Zap, title: t("page.premium.early"), desc: t("page.premium.early_d") },
    { icon: ShieldOff, title: t("page.premium.adfree"), desc: t("page.premium.adfree_d") },
  ];

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          kicker={user?.isPremium ? t("page.premium.kicker_active") : t("page.premium.kicker_up")}
          title={
            <>
              {t("page.premium.title_pre")} <span className="text-neon">{t("page.premium.title_post")}</span>
            </>
          }
          subtitle={t("page.premium.sub")}
        />

        <div className="relative overflow-hidden rounded-3xl border border-neon/40 bg-gradient-to-br from-neon/15 via-zinc-900/80 to-black p-6 sm:p-10">
          <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-neon/25 blur-3xl" />
          <div className="grid lg:grid-cols-2 gap-8 items-center relative">
            <div>
              <Crown className="text-neon" size={28} />
              <h2 className="font-display text-3xl sm:text-4xl font-black mt-3">{t("page.premium.hero_h")}</h2>
              <p className="mt-3 text-zinc-300 max-w-md">{t("page.premium.hero_sub")}</p>
              <ul className="mt-6 space-y-2">
                {PERKS.map((p) => {
                  const Icon = p.icon;
                  return (
                    <li key={p.title} className="flex items-start gap-3">
                      <span className="w-8 h-8 rounded-lg bg-neon/10 border border-neon/25 text-neon grid place-items-center shrink-0">
                        <Icon size={16} />
                      </span>
                      <div>
                        <div className="font-semibold">{p.title}</div>
                        <div className="text-sm text-zinc-400">{p.desc}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <PlanCard
                name={t("page.premium.monthly")}
                price="€6.99"
                period={t("lp.pricing.per_month")}
                cta={t("premium.upgrade_btn") || "Upgrade to Premium"}
                active={user?.isPremium}
                activeLabel={t("page.premium.active")}
                loading={loadingPlan === "monthly"}
                onClick={() => handleSubscribe("monthly")}
                testid="plan-monthly"
              />
              <PlanCard
                name={t("page.premium.annual")}
                price="$59"
                period={t("lp.pricing.per_month") === "/ month" ? "/ year" : "/ Jahr"}
                effective={t("lp.pricing.per_month") === "/ month" ? "$4.92 / mo" : "4,92 $ / Mon."}
                badge={t("page.premium.best_value") || "Beste Wahl · Spare 30%"}
                highlight
                bestValue
                cta={t("page.premium.start_yearly") || "Jährlich starten"}
                active={user?.isPremium}
                activeLabel={t("page.premium.active")}
                loading={loadingPlan === "annual"}
                onClick={() => handleSubscribe("annual")}
                testid="plan-annual"
              />
            </div>
            <p className="mt-3 text-[11px] text-zinc-500 flex items-center gap-2">
              <Check size={12} className="text-neon" />
              {t("page.premium.cancel_reassure") || "Risikofrei · jederzeit kündbar · keine versteckten Kosten."}
            </p>
          </div>
        </div>

        {isGuest && (
          <div className="mt-6 rounded-2xl border border-white/5 bg-zinc-900/50 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="premium-guest-footer">
            <p className="text-sm text-zinc-300">{t("guest.premium_footer")}</p>
            <Button
              className="w-full sm:w-auto rounded-full bg-neon text-black hover:bg-neon-hover font-bold shadow-neon"
              onClick={() => navigate("/login")}
              data-testid="premium-guest-signup"
            >
              {t("wall.signup")}
            </Button>
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-zinc-400">
          <InfoCard title={t("page.premium.cancel_anytime")} desc={t("page.premium.cancel_anytime_d")} />
          <InfoCard title={t("page.premium.secure_checkout")} desc={t("page.premium.secure_checkout_d")} />
          <InfoCard title={t("page.premium.scale")} desc={t("page.premium.scale_d")} />
        </div>
      </PageContainer>
    </AppShell>
  );
}

function PlanCard({ name, price, period, effective, badge, highlight, bestValue, cta, onClick, active, activeLabel, loading, testid }) {
  return (
    <div
      className={`relative rounded-2xl p-6 border transition-transform ${
        highlight
          ? "border-neon bg-gradient-to-b from-neon/10 to-black/60 shadow-neon scale-[1.02] sm:scale-[1.03]"
          : "border-white/10 bg-zinc-950/50"
      }`}
      data-testid={testid}
    >
      {bestValue && (
        <span
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-neon text-black text-[10px] font-black uppercase tracking-widest shadow-neon whitespace-nowrap"
          data-testid="best-value-badge"
        >
          {badge}
        </span>
      )}
      <div className="flex items-center justify-between min-h-[24px]">
        <div className="font-display font-bold text-lg">{name}</div>
        {!bestValue && badge && (
          <span className="text-[10px] uppercase tracking-widest text-neon font-bold">{badge}</span>
        )}
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="font-display text-4xl font-black">{price}</span>
        <span className="text-xs text-zinc-400">{period}</span>
      </div>
      {effective && (
        <div className="mt-1 text-[11px] text-emerald-400 font-semibold" data-testid="plan-effective">
          ≈ {effective}
        </div>
      )}
      <Button
        onClick={onClick}
        disabled={active || loading}
        data-testid={`${testid}-cta`}
        className={`mt-5 w-full h-11 rounded-full font-bold ${
          active ? "bg-white/10 text-zinc-300" : "bg-neon text-black hover:bg-neon-hover shadow-neon"
        }`}
      >
        {active ? (
          <>
            <Check size={14} className="mr-2" /> {activeLabel}
          </>
        ) : loading ? (
          "…"
        ) : (
          cta
        )}
      </Button>
    </div>
  );
}

function InfoCard({ title, desc }) {
  return (
    <div className="rounded-2xl border border-white/5 bg-zinc-900/50 p-5">
      <div className="font-display font-bold text-white">{title}</div>
      <div className="mt-1">{desc}</div>
    </div>
  );
}
